import { TypedDocumentNode, gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { StuffInRewardBoxType } from '@sorare/core/src/components/rewards/RewardBoxDialog/types';
import { RewardCount } from '@sorare/core/src/components/rewards/RewardCount';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { isType } from '@sorare/core/src/lib/gql';
import { lazy } from '@sorare/core/src/lib/retry';

import {
  EssenceInRewardBox_ProbabilisticBundleSlotItemInterface,
  EssenceInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotCardShardItem_,
} from './__generated__/index.graphql';

const EssenceInRewardBoxTrois = lazy(
  async () =>
    import('@sorare/trois/src/components/RewardBox/EssenceInRewardBox'),
  'EssenceInRewardBox'
);

const NoWrap = styled.div`
  white-space: nowrap;
`;

type EssenceInRewardBoxPreloaderProps = {
  item: EssenceInRewardBox_ProbabilisticBundleSlotItemInterface;
};

const MAP = {
  [Rarity.common]: 'common',
  [Rarity.custom_series]: 'limited',
  [Rarity.limited]: 'limited',
  [Rarity.rare]: 'rare',
  [Rarity.super_rare]: 'super_rare',
  [Rarity.unique]: 'unique',
} as const;

type Props = {
  visible: boolean;
} & EssenceInRewardBoxPreloaderProps;

const EssenceInRewardBoxCanHandle = (
  item: Props['item']
): item is EssenceInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotCardShardItem_ => {
  return isType(item, 'ProbabilisticBundleSlotCardShardItem');
};

export const EssenceInRewardBox: StuffInRewardBoxType<
  EssenceInRewardBox_ProbabilisticBundleSlotItemInterface
> = ({ item, visible }: Props) => {
  const { formatMessage } = useIntl();

  if (!EssenceInRewardBoxCanHandle(item)) {
    return null;
  }

  return (
    <EssenceInRewardBoxTrois
      visible={visible}
      rarity={MAP[item.rarity]}
      description={
        <NoWrap>
          <RewardCount
            quantity={item.quantity}
            rewardName={formatMessage(
              {
                id: 'EssenceInRewardBox.rarityEssenceCapsule',
                defaultMessage: `{count, plural, one {{rarity} Essence Capsule} other {{rarity} Essence Capsules}}`,
              },
              { count: item.quantity, rarity: scarcityNames[item.rarity] }
            )}
          />
        </NoWrap>
      }
    />
  );
};

EssenceInRewardBox.fragments = {
  ProbabilisticBundleSlotItemInterface: gql`
    fragment EssenceInRewardBox_ProbabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ... on ProbabilisticBundleSlotCardShardItem {
        id
        quantity
        rarity
      }
    }
  ` as TypedDocumentNode<EssenceInRewardBox_ProbabilisticBundleSlotItemInterface>,
};

const EssenceInRewardBoxPreloader = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  item,
}: EssenceInRewardBoxPreloaderProps) => {
  return null;
};

EssenceInRewardBox.preloader = EssenceInRewardBoxPreloader;

EssenceInRewardBox.canHandle = EssenceInRewardBoxCanHandle;

EssenceInRewardBox.description = (item: Props['item']) => {
  if (!EssenceInRewardBoxCanHandle(item)) {
    return null;
  }

  const { quantity, rarity } = item;

  return `${quantity}× ${rarity} Essence`;
};

EssenceInRewardBox.fallback = (item: Props['item']) => {
  if (!EssenceInRewardBoxCanHandle(item)) {
    return null;
  }
  return <div>{EssenceInRewardBox.description(item)}</div>;
};
